import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import env from "@beam-australia/react-env";
import useApi from "../../../../hooks/useApi";
import useSkeleton from "../../../../hooks/useSkeleton";

import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import { useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";

/**
 * Cierra una línea que quedó en TO_VERIFY: la facturación se cortó sin respuesta y nadie sabe si el
 * comprobante se emitió. El pago ya está firme, así que lo único que se decide acá es si hay que
 * emitir el comprobante o si ya existe del otro lado.
 */
const WithholdingsLinesResolveBilling = ({ settings }) => {
  const [isLoading, setIsLoading] = useAtom(loadingState);

  const api = useApi();

  const s = useSkeleton("abm");
  const { t } = useTranslation();

  const [billingReference, setBillingReference] = useState("");

  const resolve = async (voucherIssued) => {
    try {
      setIsLoading(true);

      await api.put(
        `${env("API_URL")}/withholdings/resolveLineBilling/${
          settings.resolveWithholdingLineId
        }`,
        {
          voucherIssued: voucherIssued,
          billingReference: voucherIssued ? billingReference : null,
        }
      );

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });

      settings.hideDialog();
    } catch (error) {
      settings.toast.current.show({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
      });
    }

    setIsLoading(false);
  };

  return (
    <React.Fragment>
      <div className={s.get()}>
        <div className="flex flex-column">
          <p>
            {t(
              "entities.withholdings.withholdingsLines.resolveBilling.explanation",
              { beneficiary: settings.resolveWithholdingLineBeneficiary }
            )}
          </p>

          <div className="flex flex-column mt-2">
            <label htmlFor="billingReference">
              {t(
                "entities.withholdings.withholdingsLines.resolveBilling.reference"
              )}
            </label>
            <div className="form-crud mt-2">
              <InputText
                id="billingReference"
                value={billingReference}
                onChange={(event) => setBillingReference(event.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="flex flex-column mt-4" style={{ gap: "4px" }}>
            <small>
              {t(
                "entities.withholdings.withholdingsLines.resolveBilling.issuedHelp"
              )}
            </small>
            <small>
              {t(
                "entities.withholdings.withholdingsLines.resolveBilling.notIssuedHelp"
              )}
            </small>
          </div>

          <div className="flex justify-content-between mt-4 mb-2">
            <Button
              label={t("buttons.cancel")}
              icon="pi pi-times"
              className="p-button-text btn-secondary-crud"
              onClick={() => settings.hideDialog("CANCEL")}
              type="button"
              disabled={isLoading}
            />
            <div className="flex" style={{ gap: "8px" }}>
              <Button
                label={t(
                  "entities.withholdings.withholdingsLines.resolveBilling.notIssued"
                )}
                icon="pi pi-replay"
                className="p-button-outlined"
                onClick={() => resolve(false)}
                type="button"
                disabled={isLoading}
              />
              <Button
                label={t(
                  "entities.withholdings.withholdingsLines.resolveBilling.issued"
                )}
                icon="pi pi-check"
                className="btn btn-primary btn-primary-crud"
                onClick={() => resolve(true)}
                type="button"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default WithholdingsLinesResolveBilling;
